import React, { useRef, useEffect } from 'react';
import { Image as KonvaImage, Transformer } from 'react-konva';
import useImage from 'use-image';

export const URLImage = ({ image, isSelected, onSelect, onChange, ...props }) => {
  // Only use crossOrigin for truly cross-origin URLs, not for same-origin /api/ URLs or data: URIs
  const getCrossOrigin = () => {
    if (!image.src) return undefined;
    if (image.src.startsWith('data:')) return undefined;
    try {
      const imgUrl = new URL(image.src, window.location.origin);
      if (imgUrl.origin === window.location.origin) return undefined;
    } catch (e) {}
    return 'anonymous';
  };
  const [img] = useImage(image.src, getCrossOrigin());
  const imageRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected && trRef.current && imageRef.current) {
      trRef.current.nodes([imageRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const getCropConfig = () => {
    if (!img || !props.width || !props.height) return null;
    // Don't crop logos — they need to be fully visible
    if (props.elementType === 'logoPlaceholder' || props.isLogo) return null;
    const imgAspect = img.width / img.height;
    const targetAspect = props.width / props.height;
    if (Math.abs(imgAspect - targetAspect) < 0.01) return null;
    let cropX = 0, cropY = 0, cropWidth = img.width, cropHeight = img.height;
    if (imgAspect > targetAspect) {
      cropWidth = img.height * targetAspect;
      cropX = (img.width - cropWidth) / 2;
    } else {
      cropHeight = img.width / targetAspect;
      cropY = (img.height - cropHeight) / 2;
    }
    return { x: cropX, y: cropY, width: cropWidth, height: cropHeight };
  };

  // For logos: calculate contain-fit dimensions
  const getContainSize = () => {
    if (!img || !props.width || !props.height) return null;
    if (props.elementType !== 'logoPlaceholder' && !props.isLogo) return null;
    const imgW = img.naturalWidth || img.width;
    const imgH = img.naturalHeight || img.height;
    const scaleX = props.width / imgW;
    const scaleY = props.height / imgH;
    const scale = Math.min(scaleX, scaleY);
    return {
      drawW: imgW * scale,
      drawH: imgH * scale,
      offsetX: (props.width - imgW * scale) / 2,
      offsetY: (props.height - imgH * scale) / 2,
    };
  };

  const cropConfig = getCropConfig();
  const containSize = getContainSize();

  return (
    <>
      <KonvaImage 
        image={img} 
        ref={imageRef}
        onClick={onSelect}
        onTap={onSelect}
        {...props}
        {...(containSize ? {
          x: (props.x || 0) + containSize.offsetX,
          y: (props.y || 0) + containSize.offsetY,
          width: containSize.drawW,
          height: containSize.drawH,
          crop: null,
        } : {
          crop: cropConfig,
        })}
        onDragEnd={(e) => {
          onChange({ x: e.target.x(), y: e.target.y() });
        }}
        onTransformEnd={(e) => {
          const node = e.target;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          node.scaleX(1);
          node.scaleY(1);
          onChange({
            x: node.x(),
            y: node.y(),
            width: Math.max(20, node.width() * scaleX),
            height: Math.max(20, node.height() * scaleY),
          });
        }}
      />
      {isSelected && !props.locked && (
        <Transformer
          ref={trRef}
          flipEnabled={false}
          boundBoxFunc={(oldBox, newBox) => {
            if (Math.abs(newBox.width) < 20 || Math.abs(newBox.height) < 20) return oldBox;
            return newBox;
          }}
        />
      )}
    </>
  );
};
